import { useState } from 'react';
import { cva } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Flex,
  Icon,
  Item,
  ItemActions,
  ItemContent,
  ItemTitle,
  Skeleton,
  Text,
} from '@kognitos/lattice';
import { V1AutomationStage } from '@/generated/hooks/grimoire/models';
import { useGetUserBasicInfo } from '@/generated/hooks/agora/v1/user';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { usePermissions } from '@/shared/permissions';
import { VersionHistoryBadge } from './VersionHistoryBadge';
import { VersionHistoryDescription } from './VersionHistoryDescription';
import {
  EVersionHistoryItemState,
  type IVersionHistoryItemProps,
} from './types';

const versionHistoryItemVariants = cva(
  'w-full cursor-pointer rounded-lg transition-all pt-2',
  {
    variants: {
      state: {
        [EVersionHistoryItemState.DEFAULT]: 'bg-transparent hover:bg-card/20',
        [EVersionHistoryItemState.SELECTED]: 'bg-card',
      },
    },
    defaultVariants: {
      state: EVersionHistoryItemState.DEFAULT,
    },
  },
);

export function VersionHistoryItem({
  revisionId,
  version,
  title,
  author,
  showBadge,
  stage = V1AutomationStage.AUTOMATION_STAGE_DRAFT,
  subtitle,
  onClick,
  menuItems,
  isSelected: isSelectedProp,
}: IVersionHistoryItemProps) {
  const { t } = useTranslation('automation');
  const [isHovered, setIsHovered] = useState(false);
  const [internalIsSelected, setInternalIsSelected] = useState(false);

  // Parse subtitle prop - can be string or object
  const subtitleTitle =
    typeof subtitle === 'string' ? subtitle : subtitle?.title;
  const subtitleDetails =
    typeof subtitle === 'object' ? subtitle.details : undefined;
  const [isMenuOpen, setIsMenuOpen] = useState(false); // to keep button visible while menu is open

  // Use controlled prop if provided, otherwise use internal state
  const isControlled = isSelectedProp !== undefined;
  const isSelected = isControlled ? isSelectedProp : internalIsSelected;

  // Changes Select state when component is clicked
  const handleSelect = () => {
    if (!isControlled) {
      setInternalIsSelected(prev => !prev);
    }
    onClick?.({
      revisionId,
      version,
      title,
      author,
      showBadge,
      stage,
      subtitle,
    });
  };

  // Changes Select state when component is focused using keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSelect();
    }
  };
  // Determine the current state
  const state = isSelected
    ? EVersionHistoryItemState.SELECTED
    : EVersionHistoryItemState.DEFAULT;

  // Show menu button on hover, when selected, or when menu is open
  const showMenu =
    menuItems &&
    menuItems.length > 0 &&
    (isHovered || isSelected || isMenuOpen);

  // Get user display name from user id
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const { hasPermission } = usePermissions();
  const canFork = hasPermission(['ForkPublishedAutomation'], {
    organization: currentOrganizationId,
    workspace: currentWorkspaceId,
  });
  const {
    data: userData,
    isLoading: isAuthorLoading,
    isError: isAuthorError,
  } = useGetUserBasicInfo(currentOrganizationId, author, {
    query: {
      enabled: Boolean(currentOrganizationId && author),
    },
  });

  // Use display name from API, fallback to email
  const authorName = userData?.data?.display_name || userData?.data?.email;

  const renderAuthor = () => {
    if (isAuthorLoading) {
      return <Skeleton className="h-4 w-32" />;
    }

    return (
      <Text
        level="small"
        color={isAuthorError ? 'destructive' : 'muted'} // Destructive is used to show author error state.
        data-testid="version-history-author-text"
      >
        {isAuthorError
          ? t('automation.versionHistory.error.authorError')
          : authorName}
      </Text>
    );
  };

  return (
    <Item
      role="button" // For screen readers
      tabIndex={0} // For keyboard navigation
      size="sm"
      className={versionHistoryItemVariants({ state })}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-selected={isSelected}
      data-testid="version-history-item"
      aria-label={t('automation.versionHistory.item.ariaLabel', { version })}
    >
      <ItemContent>
        <Flex align="center" justify="between" width="full" gap={2}>
          {/* w-0 overrides ItemTitle's w-fit to allow for truncation */}
          <ItemTitle className="w-0 flex-1 min-w-0 truncate">
            <Text
              level="small"
              weight="medium"
              className="truncate min-w-0"
              data-testid="version-history-title"
            >
              {title}
            </Text>
            <Text
              level="small"
              weight="medium"
              color="muted"
              className="leading-none"
              data-testid="version-history-version"
            >
              {version}
            </Text>
            {showBadge && <VersionHistoryBadge stage={stage} state={state} />}
          </ItemTitle>

          {canFork && (
            <ItemActions>
              <DropdownMenu onOpenChange={setIsMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    tabIndex={showMenu ? 0 : -1} // Will not tab over a hidden menu button
                    className={
                      showMenu ? 'visible' : 'invisible pointer-events-none'
                    }
                    onClick={e => e.stopPropagation()}
                    data-testid="version-history-menu-trigger"
                  >
                    <Icon
                      type="EllipsisVertical"
                      size="sm"
                      className="text-foreground"
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  onClick={e => e.stopPropagation()}
                >
                  {menuItems?.map(({ key, title, icon, ...menuItemProps }) => (
                    <DropdownMenuItem key={key} {...menuItemProps}>
                      {icon && <Icon type={icon} size="xs" />}
                      {title}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </ItemActions>
          )}
        </Flex>

        <Flex direction="column" gap={1.5}>
          {subtitleTitle && (
            <VersionHistoryDescription
              title={subtitleTitle}
              details={subtitleDetails}
            />
          )}

          <Flex align="center" justify="start" gap={2}>
            <Icon type="User" size="xs" className="text-muted-foreground" />
            {renderAuthor()}
          </Flex>
        </Flex>
      </ItemContent>
    </Item>
  );
}
